/**
 * Preset Icons for Service Options
 *
 * Curated collection of icons from react-icons library for service booking options.
 * Icons are organized by category for easy selection in the IconPickerDropdown.
 *
 * To add more icons:
 * 1. Import the icon from react-icons (e.g., import { MdNewIcon } from 'react-icons/md')
 * 2. Add an entry to PRESET_ICONS with a unique key
 * 3. Optionally add to a category in ICON_CATEGORIES
 *
 * Color handling:
 * Icons use the brand color passed via props, with fallback hierarchy:
 * Meeting Type brandColor > Organization brandColor > SailLab default (#2E7AA0)
 */

import { IconType } from 'react-icons';

// Material Design Icons (comprehensive, clean design)
import {
  MdWork,
  MdBusinessCenter,
  MdHandshake,
  MdGroups,
  MdPresentToAll,
  MdAccountBalance,
  MdAssignment,
  MdBadge,
  MdContactMail,
  MdSupport,
  MdAssessment,
  MdLightbulb,
  MdGavel,
  MdPolicy,
  MdSchool,
  MdMenuBook,
  MdHistoryEdu,
  MdScience,
  MdCalculate,
  MdDraw,
  MdPiano,
  MdMusicNote,
  MdLanguage,
  MdCastForEducation,
  MdAutoStories,
  MdQuiz,
  MdFitnessCenter,
  MdSportsGymnastics,
  MdSportsMartialArts,
  MdDirectionsRun,
  MdPool,
  MdSportsTennis,
  MdSportsGolf,
  MdSelfImprovement,
  MdSportsKabaddi,
  MdAccessibility,
  MdComputer,
  MdSmartphone,
  MdCode,
  MdBuild,
  MdSettings,
  MdDeveloperMode,
  MdMemory,
  MdWifi,
  MdCloud,
  MdSecurity,
  MdBugReport,
  MdIntegrationInstructions,
  MdSpa,
  MdFace,
  MdContentCut,
  MdBrush,
  MdColorLens,
  MdAutoAwesome,
  MdFaceRetouchingNatural,
  MdDirectionsCar,
  MdTwoWheeler,
  MdDirectionsBike,
  MdLocalTaxi,
  MdAirportShuttle,
  MdFlight,
  MdDirectionsBoat,
  MdTrain,
  MdStar,
  MdFavorite,
  MdCheckCircle,
  MdCalendarToday,
  MdAccessTime,
  MdLocationOn,
  MdLocalOffer,
  MdEmojiEvents,
  MdVolunteerActivism,
  MdCelebration,
} from 'react-icons/md';

// Font Awesome Icons (additional variety)
import {
  FaBriefcase,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaDumbbell,
  FaRunning,
  FaBicycle,
  FaSwimmer,
  FaLaptopCode,
  FaTools,
  FaCut,
  FaPaintBrush,
  FaMagic,
  FaCar,
  FaMotorcycle,
  FaPlane,
  FaShip,
  FaHeart,
  FaStar,
  FaCalendarCheck,
  FaClock,
  FaGift,
  FaTrophy,
} from 'react-icons/fa';

// Bootstrap Icons (only importing used ones)
import {
  BsScissors,
  BsPalette,
} from 'react-icons/bs';

/**
 * Icon category definitions
 */
export type IconCategory =
  | 'professional'
  | 'education'
  | 'fitness'
  | 'tech'
  | 'beauty'
  | 'transportation'
  | 'general';

/**
 * Single preset icon definition
 */
export interface PresetIconDef {
  icon: IconType;
  category: IconCategory;
  labelKey: string; // i18n key for the label
  labelFallback: string; // English fallback
}

/**
 * All preset icons mapped by their unique key
 * Key format: library prefix + icon name (e.g., "MdWork", "FaBriefcase")
 */
export const PRESET_ICONS: Record<string, PresetIconDef> = {
  // ============================================
  // PROFESSIONAL (15 icons)
  // ============================================
  MdWork: {
    icon: MdWork,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.work',
    labelFallback: 'Work',
  },
  MdBusinessCenter: {
    icon: MdBusinessCenter,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.business',
    labelFallback: 'Business',
  },
  MdHandshake: {
    icon: MdHandshake,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.handshake',
    labelFallback: 'Handshake',
  },
  MdGroups: {
    icon: MdGroups,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.team',
    labelFallback: 'Team',
  },
  MdPresentToAll: {
    icon: MdPresentToAll,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.presentation',
    labelFallback: 'Presentation',
  },
  MdAccountBalance: {
    icon: MdAccountBalance,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.finance',
    labelFallback: 'Finance',
  },
  MdAssignment: {
    icon: MdAssignment,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.assignment',
    labelFallback: 'Assignment',
  },
  MdBadge: {
    icon: MdBadge,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.badge',
    labelFallback: 'Badge',
  },
  MdContactMail: {
    icon: MdContactMail,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.contact',
    labelFallback: 'Contact',
  },
  MdSupport: {
    icon: MdSupport,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.support',
    labelFallback: 'Support',
  },
  MdAssessment: {
    icon: MdAssessment,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.analytics',
    labelFallback: 'Analytics',
  },
  MdLightbulb: {
    icon: MdLightbulb,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.idea',
    labelFallback: 'Idea',
  },
  MdGavel: {
    icon: MdGavel,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.legal',
    labelFallback: 'Legal',
  },
  MdPolicy: {
    icon: MdPolicy,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.policy',
    labelFallback: 'Policy',
  },
  FaBriefcase: {
    icon: FaBriefcase,
    category: 'professional',
    labelKey: 'meetingTypes:iconPicker.icons.briefcase',
    labelFallback: 'Briefcase',
  },

  // ============================================
  // EDUCATION (14 icons)
  // ============================================
  MdSchool: {
    icon: MdSchool,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.school',
    labelFallback: 'School',
  },
  MdMenuBook: {
    icon: MdMenuBook,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.textbook',
    labelFallback: 'Textbook',
  },
  MdHistoryEdu: {
    icon: MdHistoryEdu,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.history',
    labelFallback: 'History',
  },
  MdScience: {
    icon: MdScience,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.science',
    labelFallback: 'Science',
  },
  MdCalculate: {
    icon: MdCalculate,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.math',
    labelFallback: 'Math',
  },
  MdDraw: {
    icon: MdDraw,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.art',
    labelFallback: 'Art',
  },
  MdPiano: {
    icon: MdPiano,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.piano',
    labelFallback: 'Piano',
  },
  MdMusicNote: {
    icon: MdMusicNote,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.music',
    labelFallback: 'Music',
  },
  MdLanguage: {
    icon: MdLanguage,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.language',
    labelFallback: 'Language',
  },
  MdCastForEducation: {
    icon: MdCastForEducation,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.onlineLearning',
    labelFallback: 'Online Learning',
  },
  MdAutoStories: {
    icon: MdAutoStories,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.reading',
    labelFallback: 'Reading',
  },
  MdQuiz: {
    icon: MdQuiz,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.quiz',
    labelFallback: 'Quiz',
  },
  FaGraduationCap: {
    icon: FaGraduationCap,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.graduation',
    labelFallback: 'Graduation',
  },
  FaChalkboardTeacher: {
    icon: FaChalkboardTeacher,
    category: 'education',
    labelKey: 'meetingTypes:iconPicker.icons.teacher',
    labelFallback: 'Teacher',
  },

  // ============================================
  // FITNESS (14 icons)
  // ============================================
  MdFitnessCenter: {
    icon: MdFitnessCenter,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.gym',
    labelFallback: 'Gym',
  },
  MdSportsGymnastics: {
    icon: MdSportsGymnastics,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.gymnastics',
    labelFallback: 'Gymnastics',
  },
  MdSportsMartialArts: {
    icon: MdSportsMartialArts,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.martialArts',
    labelFallback: 'Martial Arts',
  },
  MdDirectionsRun: {
    icon: MdDirectionsRun,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.running',
    labelFallback: 'Running',
  },
  MdPool: {
    icon: MdPool,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.swimming',
    labelFallback: 'Swimming',
  },
  MdSportsTennis: {
    icon: MdSportsTennis,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.tennis',
    labelFallback: 'Tennis',
  },
  MdSportsGolf: {
    icon: MdSportsGolf,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.golf',
    labelFallback: 'Golf',
  },
  MdSelfImprovement: {
    icon: MdSelfImprovement,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.yoga',
    labelFallback: 'Yoga',
  },
  MdSportsKabaddi: {
    icon: MdSportsKabaddi,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.training',
    labelFallback: 'Training',
  },
  MdAccessibility: {
    icon: MdAccessibility,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.stretching',
    labelFallback: 'Stretching',
  },
  FaDumbbell: {
    icon: FaDumbbell,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.weights',
    labelFallback: 'Weights',
  },
  FaRunning: {
    icon: FaRunning,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.jogging',
    labelFallback: 'Jogging',
  },
  FaBicycle: {
    icon: FaBicycle,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.cycling',
    labelFallback: 'Cycling',
  },
  FaSwimmer: {
    icon: FaSwimmer,
    category: 'fitness',
    labelKey: 'meetingTypes:iconPicker.icons.swimmer',
    labelFallback: 'Swimmer',
  },

  // ============================================
  // TECH (14 icons)
  // ============================================
  MdComputer: {
    icon: MdComputer,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.computer',
    labelFallback: 'Computer',
  },
  MdSmartphone: {
    icon: MdSmartphone,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.smartphone',
    labelFallback: 'Smartphone',
  },
  MdCode: {
    icon: MdCode,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.code',
    labelFallback: 'Code',
  },
  MdBuild: {
    icon: MdBuild,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.repair',
    labelFallback: 'Repair',
  },
  MdSettings: {
    icon: MdSettings,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.settings',
    labelFallback: 'Settings',
  },
  MdDeveloperMode: {
    icon: MdDeveloperMode,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.development',
    labelFallback: 'Development',
  },
  MdMemory: {
    icon: MdMemory,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.hardware',
    labelFallback: 'Hardware',
  },
  MdWifi: {
    icon: MdWifi,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.network',
    labelFallback: 'Network',
  },
  MdCloud: {
    icon: MdCloud,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.cloud',
    labelFallback: 'Cloud',
  },
  MdSecurity: {
    icon: MdSecurity,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.security',
    labelFallback: 'Security',
  },
  MdBugReport: {
    icon: MdBugReport,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.debugging',
    labelFallback: 'Debugging',
  },
  MdIntegrationInstructions: {
    icon: MdIntegrationInstructions,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.integration',
    labelFallback: 'Integration',
  },
  FaLaptopCode: {
    icon: FaLaptopCode,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.programming',
    labelFallback: 'Programming',
  },
  FaTools: {
    icon: FaTools,
    category: 'tech',
    labelKey: 'meetingTypes:iconPicker.icons.tools',
    labelFallback: 'Tools',
  },

  // ============================================
  // BEAUTY (14 icons)
  // ============================================
  MdSpa: {
    icon: MdSpa,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.spa',
    labelFallback: 'Spa',
  },
  MdFace: {
    icon: MdFace,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.facial',
    labelFallback: 'Facial',
  },
  MdContentCut: {
    icon: MdContentCut,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.haircut',
    labelFallback: 'Haircut',
  },
  MdBrush: {
    icon: MdBrush,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.makeup',
    labelFallback: 'Makeup',
  },
  MdColorLens: {
    icon: MdColorLens,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.coloring',
    labelFallback: 'Coloring',
  },
  MdAutoAwesome: {
    icon: MdAutoAwesome,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.sparkle',
    labelFallback: 'Sparkle',
  },
  MdFaceRetouchingNatural: {
    icon: MdFaceRetouchingNatural,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.skincare',
    labelFallback: 'Skincare',
  },
  FaCut: {
    icon: FaCut,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.scissors',
    labelFallback: 'Scissors',
  },
  FaPaintBrush: {
    icon: FaPaintBrush,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.brush',
    labelFallback: 'Brush',
  },
  FaMagic: {
    icon: FaMagic,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.magic',
    labelFallback: 'Magic',
  },
  BsScissors: {
    icon: BsScissors,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.styling',
    labelFallback: 'Styling',
  },
  BsPalette: {
    icon: BsPalette,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.palette',
    labelFallback: 'Palette',
  },
  MdVolunteerActivism: {
    icon: MdVolunteerActivism,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.wellness',
    labelFallback: 'Wellness',
  },
  MdCelebration: {
    icon: MdCelebration,
    category: 'beauty',
    labelKey: 'meetingTypes:iconPicker.icons.celebration',
    labelFallback: 'Celebration',
  },

  // ============================================
  // TRANSPORTATION (12 icons)
  // ============================================
  MdDirectionsCar: {
    icon: MdDirectionsCar,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.car',
    labelFallback: 'Car',
  },
  MdTwoWheeler: {
    icon: MdTwoWheeler,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.scooter',
    labelFallback: 'Scooter',
  },
  MdDirectionsBike: {
    icon: MdDirectionsBike,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.bike',
    labelFallback: 'Bike',
  },
  MdLocalTaxi: {
    icon: MdLocalTaxi,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.taxi',
    labelFallback: 'Taxi',
  },
  MdAirportShuttle: {
    icon: MdAirportShuttle,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.shuttle',
    labelFallback: 'Shuttle',
  },
  MdFlight: {
    icon: MdFlight,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.flight',
    labelFallback: 'Flight',
  },
  MdDirectionsBoat: {
    icon: MdDirectionsBoat,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.boat',
    labelFallback: 'Boat',
  },
  MdTrain: {
    icon: MdTrain,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.train',
    labelFallback: 'Train',
  },
  FaCar: {
    icon: FaCar,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.automobile',
    labelFallback: 'Automobile',
  },
  FaMotorcycle: {
    icon: FaMotorcycle,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.motorcycle',
    labelFallback: 'Motorcycle',
  },
  FaPlane: {
    icon: FaPlane,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.plane',
    labelFallback: 'Plane',
  },
  FaShip: {
    icon: FaShip,
    category: 'transportation',
    labelKey: 'meetingTypes:iconPicker.icons.ship',
    labelFallback: 'Ship',
  },

  // ============================================
  // GENERAL (14 icons)
  // ============================================
  MdStar: {
    icon: MdStar,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.star',
    labelFallback: 'Star',
  },
  MdFavorite: {
    icon: MdFavorite,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.heart',
    labelFallback: 'Heart',
  },
  MdCheckCircle: {
    icon: MdCheckCircle,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.check',
    labelFallback: 'Check',
  },
  MdCalendarToday: {
    icon: MdCalendarToday,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.calendar',
    labelFallback: 'Calendar',
  },
  MdAccessTime: {
    icon: MdAccessTime,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.time',
    labelFallback: 'Time',
  },
  MdLocationOn: {
    icon: MdLocationOn,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.location',
    labelFallback: 'Location',
  },
  MdLocalOffer: {
    icon: MdLocalOffer,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.offer',
    labelFallback: 'Offer',
  },
  MdEmojiEvents: {
    icon: MdEmojiEvents,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.trophy',
    labelFallback: 'Trophy',
  },
  FaHeart: {
    icon: FaHeart,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.love',
    labelFallback: 'Love',
  },
  FaStar: {
    icon: FaStar,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.favorite',
    labelFallback: 'Favorite',
  },
  FaCalendarCheck: {
    icon: FaCalendarCheck,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.appointment',
    labelFallback: 'Appointment',
  },
  FaClock: {
    icon: FaClock,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.clock',
    labelFallback: 'Clock',
  },
  FaGift: {
    icon: FaGift,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.gift',
    labelFallback: 'Gift',
  },
  FaTrophy: {
    icon: FaTrophy,
    category: 'general',
    labelKey: 'meetingTypes:iconPicker.icons.award',
    labelFallback: 'Award',
  },
};

/**
 * Category metadata for the icon picker UI
 */
export interface CategoryMeta {
  key: IconCategory;
  labelKey: string;
  labelFallback: string;
  order: number;
}

/**
 * Ordered list of categories for display
 */
export const ICON_CATEGORIES: CategoryMeta[] = [
  {
    key: 'professional',
    labelKey: 'meetingTypes:iconPicker.categories.professional',
    labelFallback: 'Professional',
    order: 1,
  },
  {
    key: 'education',
    labelKey: 'meetingTypes:iconPicker.categories.education',
    labelFallback: 'Education',
    order: 2,
  },
  {
    key: 'fitness',
    labelKey: 'meetingTypes:iconPicker.categories.fitness',
    labelFallback: 'Fitness',
    order: 3,
  },
  {
    key: 'tech',
    labelKey: 'meetingTypes:iconPicker.categories.tech',
    labelFallback: 'Tech',
    order: 4,
  },
  {
    key: 'beauty',
    labelKey: 'meetingTypes:iconPicker.categories.beauty',
    labelFallback: 'Beauty',
    order: 5,
  },
  {
    key: 'transportation',
    labelKey: 'meetingTypes:iconPicker.categories.transportation',
    labelFallback: 'Transportation',
    order: 6,
  },
  {
    key: 'general',
    labelKey: 'meetingTypes:iconPicker.categories.general',
    labelFallback: 'General',
    order: 7,
  },
];

/**
 * Get icons filtered by category
 */
export function getIconsByCategory(category: IconCategory | 'all'): Array<{ key: string; def: PresetIconDef }> {
  return Object.entries(PRESET_ICONS)
    .filter(([, def]) => category === 'all' || def.category === category)
    .map(([key, def]) => ({ key, def }));
}

/**
 * Search icons by label (supports both i18n key lookup and fallback)
 */
export function searchIcons(
  query: string,
  t?: (key: string, fallback: string) => string
): Array<{ key: string; def: PresetIconDef }> {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) {
    return Object.entries(PRESET_ICONS).map(([key, def]) => ({ key, def }));
  }

  return Object.entries(PRESET_ICONS)
    .filter(([key, def]) => {
      // Search in icon key
      if (key.toLowerCase().includes(normalizedQuery)) return true;
      // Search in fallback label
      if (def.labelFallback.toLowerCase().includes(normalizedQuery)) return true;
      // Search in translated label if translation function provided
      if (t) {
        const translatedLabel = t(def.labelKey, def.labelFallback);
        if (translatedLabel.toLowerCase().includes(normalizedQuery)) return true;
      }
      return false;
    })
    .map(([key, def]) => ({ key, def }));
}

/**
 * Get a specific icon by its key
 */
export function getPresetIcon(iconKey: string): PresetIconDef | undefined {
  return PRESET_ICONS[iconKey];
}

/**
 * Check if a string is a valid preset icon key
 */
export function isPresetIconKey(key: string): boolean {
  return key in PRESET_ICONS;
}

/**
 * Total count of preset icons
 */
export const PRESET_ICON_COUNT = Object.keys(PRESET_ICONS).length;

/**
 * Default brand color (SailLab)
 */
export const DEFAULT_ICON_COLOR = '#2E7AA0';
